import {OverrideDay, Weekday} from "@/api";
import {WEEKDAY_MESSAGES} from "@/utils/translations";
import {FormattedButton, FormattedFormRow, FormContainer, FormDialogActionButtons} from "@dvrt/graphql-widgets";
import {Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select} from "@mui/material";
import deepEqual from "deep-equal";
import {DateTime} from "luxon";

const WEEKDAYS = [
    Weekday.Monday,
    Weekday.Tuesday,
    Weekday.Wednesday,
    Weekday.Thursday,
    Weekday.Friday,
    Weekday.Saturday,
    Weekday.Sunday,
] as const;

import {useCallback, useMemo, useState} from "react";
import {FormattedMessage, defineMessage} from "react-intl";

const getWeekdayFromDate = (date: Date): Weekday => {
    switch (date.getUTCDay()) {
        case 0:
            return Weekday.Monday;
        case 1:
            return Weekday.Tuesday;
        case 2:
            return Weekday.Wednesday;
        case 3:
            return Weekday.Thursday;
        case 4:
            return Weekday.Friday;
        case 5:
            return Weekday.Saturday;
        case 6:
            return Weekday.Sunday;
        default:
            throw new Error("Invalid day number");
    }
};

type AddSpecialDayDialogProps = {
    date: Date;
    onSave: (day: OverrideDay) => Promise<void>;
};

const addSpecialDayInitialValue = (date: Date): OverrideDay => ({
    day: DateTime.fromJSDate(date).toISODate(),
    considerAs: getWeekdayFromDate(date),
    everyYear: false,
});

const Content = ({date, onSave, close}: AddSpecialDayDialogProps & {close: () => void}) => {
    const initialData = addSpecialDayInitialValue(date);
    const [saving, setSaving] = useState(false);
    const [value, setValue] = useState<OverrideDay>(initialData);

    const isChanged = useMemo(() => !deepEqual(value, initialData), [value, initialData]);

    const handleSave = useCallback(async () => {
        setSaving(true);
        await onSave(value);
        setSaving(false);
    }, [onSave, value]);

    return (
        <>
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow
                        label={defineMessage({id: "specialDays.add.overrideWith", defaultMessage: "Override with"})}
                    >
                        <Select<Weekday>
                            value={value.considerAs}
                            onChange={(event) =>
                                setValue((prev) => ({...prev, considerAs: event.target.value as Weekday}))
                            }
                        >
                            {WEEKDAYS.map((weekday) => (
                                <MenuItem key={weekday} value={weekday}>
                                    <FormattedMessage {...WEEKDAY_MESSAGES[weekday]} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "specialDays.add.everyYear", defaultMessage: "Every year"})}
                    >
                        <Checkbox
                            checked={value.everyYear}
                            onChange={(event) => setValue((prev) => ({...prev, everyYear: event.target.checked}))}
                        />
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <FormDialogActionButtons
                    mainAction={async () => {
                        await handleSave();
                        close();
                        setValue(initialData);
                    }}
                    secondaryAction={() => setValue(addSpecialDayInitialValue(date))}
                    cancel={() => {
                        close();
                        setValue(initialData);
                    }}
                    mainButtonEnabled={isChanged && !saving}
                    secondaryButtonEnabled={isChanged && !saving}
                    pending={saving}
                />
            </DialogActions>
        </>
    );
};

const AddSpecialDay = ({date, onSave}: AddSpecialDayDialogProps) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <Dialog open={open}>
                <DialogTitle></DialogTitle>
                <Content date={date} onSave={onSave} close={() => setOpen(false)} />
            </Dialog>
            <FormattedButton
                message={defineMessage({id: "add", defaultMessage: "Add"})}
                onClick={() => setOpen((prev) => !prev)}
            />
        </>
    );
};

export default AddSpecialDay;
